/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Flex, FormControl, Heading, Text } from "@chakra-ui/react";
import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
import { Field, Formik } from "formik";
import { AuthInput } from "components/auth/AuthInput";

import { ErrorAlert } from "./components/ErrorAlert";
import { GoogleLoginButton } from "./components/GoogleLoginButton";
import { FormFooter } from "./components/FormFooter";
import { useSignInStyles } from "./hooks/useSignInStyles";
import { useSignInLogic } from "./hooks/useSignInLogic";
import { validationSchema, initialValues } from "./constants";

export default function SignIn() {
  const {
    navigate,
    authStyles,
    isLoading,
    isDoingLogin,
    showError,
    authErrorMessageText,
    handleGoogleLogin,
    handleFormSubmit,
  } = useSignInLogic();

  const styles = useSignInStyles(authStyles);

  return (
    <DefaultAuth>
      <Flex
        maxW={authStyles.container?.maxW || "420px"}
        w={authStyles.container?.w || "100%"}
        mx="auto"
        my="auto"
        h="fit-content"
        alignItems="start"
        justifyContent="center"
        flexDirection="column"
        borderRadius="12px"
        border="1px solid #E6ECF3"
        bgColor="#F5F7FA"
        fontFamily="General Sans, Inter, sans-serif"
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleFormSubmit}
        >
          {({ handleSubmit, errors, submitCount }) => (
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              {/* Form Content */}
              <Flex
                zIndex="2"
                direction="column"
                padding="40px 32px 24px"
                w="full"
                borderRadius="12px"
              >
                <Box textAlign="center">
                  {showError && authErrorMessageText && (
                    <ErrorAlert message={authErrorMessageText} />
                  )}

                  <Heading
                    color={styles.textColor}
                    fontFamily={authStyles.heading?.fontFamily || "Canela Text"}
                    fontSize={authStyles.heading?.fontSize || "30px"}
                    fontWeight={authStyles.heading?.fontWeight}
                    lineHeight={authStyles.heading?.lineHeight || "135%"}
                    mb="48px"
                    textAlign={authStyles.heading?.textAlign || "center"}
                  >
                    Log in to Languex
                  </Heading>
                </Box>

                {/* Google Login Button */}
                <GoogleLoginButton
                  authStyles={authStyles}
                  styles={styles}
                  isLoading={isLoading}
                  onClick={() => handleGoogleLogin()}
                />

                {/* Separator */}
                <Flex align="center" my="9.5px">
                  <HSeparator />
                  <Text
                    fontSize="14px"
                    color="#0B0E10"
                    p="3px 8px"
                    textTransform="uppercase"
                    whiteSpace="nowrap"
                  >
                    OR
                  </Text>
                  <HSeparator />
                </Flex>

                {/* Email Field */}
                <FormControl
                  isInvalid={submitCount > 0 && !!errors.email}
                  mb="12px"
                >
                  <Field name="email">
                    {({ field }: any) => (
                      <AuthInput
                        {...field}
                        role="customer"
                        chakraVariant="authCustomerNew"
                        type="email"
                        id="email"
                        placeholder="Email Address"
                        isInvalid={submitCount > 0 && !!errors.email}
                        onChange={(e: any) => {
                          field.onChange(e);
                        }}
                      />
                    )}
                  </Field>
                </FormControl>

                {/* Password Field */}
                <FormControl
                  isInvalid={submitCount > 0 && !!errors.password}
                  mb="12px"
                >
                  <Field name="password">
                    {({ field }: any) => (
                      <AuthInput
                        {...field}
                        role="customer"
                        chakraVariant="authCustomerNew"
                        type="password"
                        id="password"
                        placeholder="Password"
                        isInvalid={submitCount > 0 && !!errors.password}
                        onChange={(e: any) => {
                          field.onChange(e);
                        }}
                      />
                    )}
                  </Field>
                </FormControl>

                {/* Forgot Password Link */}
                <Flex justifyContent="start" align="center">
                  <Text
                    color={styles.textColorBrand}
                    fontSize="14px"
                    lineHeight="17px"
                    fontWeight="500"
                    cursor="pointer"
                    textDecoration="underline"
                    onClick={() => navigate("/auth/forgot")}
                  >
                    Forgot your email or password?
                  </Text>
                </Flex>
              </Flex>

              {/* Footer */}
              <FormFooter
                authStyles={authStyles}
                styles={styles}
                isLoading={isDoingLogin}
                onSignUp={() => navigate("/auth/sign-up")}
              />
            </form>
          )}
        </Formik>
      </Flex>
    </DefaultAuth>
  );
}
